import { yieldToMainThread } from '@theme/utilities';
import { Component } from '@theme/component';

let lastLaunchPoint;

document.addEventListener(
  'pointerdown',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest(
      'button[type="submit"], .add-to-cart-button, .quick-add__button, [name="add"], [data-hanfi-add-to-cart]'
    );
    if (!button) return;
    lastLaunchPoint = {
      x: event.clientX,
      y: event.clientY,
      time: performance.now(),
    };
  },
  true
);

/**
 * FlyToCart custom element for animating product images to cart
 * This component creates a visual effect of a product "flying" to the cart when added
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  /** @type {{x: number, y: number} | undefined} */
  startPoint;

  connectedCallback() {
    super.connectedCallback();
    if (!this.source || !this.destination) {
      this.remove();
      return;
    }

    window.requestAnimationFrame(() => {
      this.#animate(this.source.getBoundingClientRect(), this.destination.getBoundingClientRect());
    });
  }

  /**
   * Animates the flying thingy along the bezier curve.
   * @param {DOMRectReadOnly} sourceRect - The bounding client rect of the source.
   * @param {DOMRectReadOnly} destinationRect - The bounding client rect of the destination.
   */
  #animate = async (sourceRect, destinationRect) => {
    const recentLaunchPoint = lastLaunchPoint && performance.now() - lastLaunchPoint.time < 1500 ? lastLaunchPoint : undefined;
    const startPoint = {
      x: this.startPoint?.x ?? recentLaunchPoint?.x ?? sourceRect.left + sourceRect.width / 2,
      y: this.startPoint?.y ?? recentLaunchPoint?.y ?? sourceRect.top + sourceRect.height / 2,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    if (this.useSourceSize) {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    }

    await yieldToMainThread();

    const width = Number.parseFloat(getComputedStyle(this).width) || 72;
    const height = Number.parseFloat(getComputedStyle(this).height) || 72;
    const startX = startPoint.x - width / 2;
    const startY = startPoint.y - height / 2;
    const endX = endPoint.x - width / 2;
    const endY = endPoint.y - height / 2;
    const distance = Math.hypot(endX - startX, endY - startY);
    const lift = Math.min(260, Math.max(130, distance * 0.24));
    const launchX = startX + (endX - startX) * 0.1;
    const launchY = startY + (endY - startY) * 0.1 - lift * 0.22;
    const midX = startX + (endX - startX) * 0.5;
    const midY = Math.min(startY, endY) - lift;
    const settleX = startX + (endX - startX) * 0.88;
    const settleY = startY + (endY - startY) * 0.88 - lift * 0.12;

    const animation = this.animate(
      [
        {
          transform: `translate3d(${startX}px, ${startY}px, 0) scale(0.82) rotate(-2deg)`,
          borderRadius: '20px',
          opacity: 0,
          offset: 0,
        },
        {
          transform: `translate3d(${startX}px, ${startY}px, 0) scale(1) rotate(0deg)`,
          borderRadius: '22px',
          opacity: 1,
          offset: 0.1,
        },
        {
          transform: `translate3d(${launchX}px, ${launchY}px, 0) scale(0.72) rotate(1deg)`,
          borderRadius: '999px',
          opacity: 1,
          offset: 0.24,
        },
        {
          transform: `translate3d(${midX}px, ${midY}px, 0) scale(0.5) rotate(8deg)`,
          borderRadius: '999px',
          opacity: 0.96,
          offset: 0.58,
        },
        {
          transform: `translate3d(${settleX}px, ${settleY}px, 0) scale(0.28) rotate(14deg)`,
          borderRadius: '999px',
          opacity: 0.9,
          offset: 0.84,
        },
        {
          transform: `translate3d(${endX}px, ${endY}px, 0) scale(0.18) rotate(18deg)`,
          borderRadius: '999px',
          opacity: 0.92,
          offset: 0.94,
        },
        {
          transform: `translate3d(${endX}px, ${endY}px, 0) scale(0.08) rotate(18deg)`,
          borderRadius: '999px',
          opacity: 0,
          offset: 1,
        },
      ],
      {
        duration: this.classList.contains('fly-to-cart--sticky') ? 1080 : 1180,
        easing: 'cubic-bezier(0.77, 0, 0.18, 1)',
        fill: 'both',
      }
    );

    const completion = animation.finished.catch(() => {});
    window.__hanfiLastFlyToCartPromise = completion;
    await completion;
    window.dispatchEvent(new CustomEvent('hanfi:fly-to-cart:complete'));
    this.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
